import React, { useState, useEffect } from "react";
import { getHomepageCards, createHomepageCard, updateHomepageCard, deleteHomepageCard } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/HomepageManager.css";

const HomepageManager = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    en: { title: "", description: "" },
    vi: { title: "", description: "" },
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    setLoading(true);
    try {
      const data = await getHomepageCards();
      setCards(data);
    } catch (err) {
      setToast({ message: "Failed to load homepage cards", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleMetaChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateHomepageCard(editingId, formData);
      } else {
        await createHomepageCard(formData);
      }
      fetchCards();
      resetForm();
      setToast({ message: "Homepage card saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save homepage card", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this card?")) {
      try {
        await deleteHomepageCard(id);
        fetchCards();
        setToast({ message: "Homepage card deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete card", type: "error" });
      }
    }
  };

  const handleEdit = (card) => {
    setFormData({
      slug: card.slug,
      en: card.translations.find((t) => t.language === "en") || { title: "", description: "" },
      vi: card.translations.find((t) => t.language === "vi") || { title: "", description: "" },
      is_active: card.is_active,
      sort_order: card.sort_order,
    });
    setEditingId(card.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      slug: "",
      en: { title: "", description: "" },
      vi: { title: "", description: "" },
      is_active: true,
      sort_order: 0,
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <div className="admin-manager-loading">Loading homepage cards...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? "Cancel" : "Add New Card"}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? "Edit Card" : "Create New Card"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group full-width">
              <label>Slug (URL identifier)</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => handleMetaChange("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                placeholder="card-slug"
                required
              />
            </div>

            <div className="form-section">
              <h4>English</h4>
              <div className="form-group full-width">
                <label>Title</label>
                <input
                  type="text"
                  value={formData.en.title}
                  onChange={(e) => handleInputChange("en", "title", e.target.value)}
                  placeholder="Card title"
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={formData.en.description}
                  onChange={(e) => handleInputChange("en", "description", e.target.value)}
                  placeholder="Card description"
                  required
                />
              </div>
            </div>

            <div className="form-section">
              <h4>Vietnamese</h4>
              <div className="form-group full-width">
                <label>Title</label>
                <input
                  type="text"
                  value={formData.vi.title}
                  onChange={(e) => handleInputChange("vi", "title", e.target.value)}
                  placeholder="Card title"
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={formData.vi.description}
                  onChange={(e) => handleInputChange("vi", "description", e.target.value)}
                  placeholder="Card description"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={formData.sort_order} onChange={(e) => handleMetaChange("sort_order", parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Active</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleMetaChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Create"} Card
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Slug</th>
              <th>English Title</th>
              <th>Vietnamese Title</th>
              <th>Active</th>
              <th>Sort Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {cards.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: "20px" }}>
                  No homepage cards found
                </td>
              </tr>
            ) : (
              cards.map((card, index) => (
                <tr key={card.id}>
                  <td>{index + 1}</td>
                  <td>{card.slug}</td>
                  <td>{card.translations.find((t) => t.language === "en")?.title || "-"}</td>
                  <td>{card.translations.find((t) => t.language === "vi")?.title || "-"}</td>
                  <td>{card.is_active ? "Yes" : "No"}</td>
                  <td>{card.sort_order}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(card)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(card.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HomepageManager;
